import { useMemo, useState } from "react";
import { Integration, User } from "../../models/Entities";
import clsx from "clsx";

export enum AvatarSize {
  xxl = "xxl",
  xl = "xl",
  normal = "normal",
  sm = "sm",
  xs = "xs",
  xxs = "xxs",
}

interface UserAvatarProps {
  user?: User | undefined;
  email?: string;
  size?: AvatarSize;
  className?: string;
}

const AvatarClassNamesMap = {
  [AvatarSize.xxl]: "w-20 h-20 text-3xl",
  [AvatarSize.xl]: "w-16 h-16 text-2xl",
  [AvatarSize.normal]: "w-10 h-10 text-xl",
  [AvatarSize.sm]: "w-9 h-9 text-l",
  [AvatarSize.xs]: "w-8 h-8 text-base",
  [AvatarSize.xxs]: "w-6 h-6 text-xs",
};

export const UserAvatar = ({
  user,
  email,
  size = AvatarSize.normal,
  className = "",
}: UserAvatarProps) => {
  const [avatarLetters, setAvatarLetters] = useState("");

  useMemo(() => {
    if (user) {
      if (user.name) {
        const splitName = user.name.split(" ");
        if (splitName.length > 1) {
          setAvatarLetters(splitName[0].charAt(0) + splitName[splitName.length - 1].charAt(0));
        }
      } else if (user.email) {
        setAvatarLetters(user.email.charAt(0).toUpperCase());
      }
    } else if (email) {
      setAvatarLetters(email.charAt(0).toUpperCase());
    }
  }, [user, email]);

  return user?.avatar ? (
    <div className={`relative inline-flex ${AvatarClassNamesMap[size]} ${className}`}>
      <img alt="avatar" src={user.avatar} className="w-full h-full object-cover rounded-full" />
    </div>
  ) : (
    <div
      className={clsx(
        "shadow-md flex items-center justify-center rounded-full",
        AvatarClassNamesMap[size],
        className
      )}
    >
      <span className="text-black font-semibold">{avatarLetters}</span>
    </div>
  );
};

interface IntegrationAvatarProps {
  integration?: Integration | undefined;
  integrationName?: string;
  squareImage?: string;
  size?: AvatarSize;
  className?: string;
}

export const IntegrationAvatar = ({
  integration,
  integrationName,
  squareImage,
  size = AvatarSize.normal,
  className = "",
}: IntegrationAvatarProps) => {
  const [avatarLetters, setAvatarLetters] = useState("");

  const resolvedName = integration?.name || integrationName || "";
  const resolvedSquareImage = integration?.square_image || squareImage;

  useMemo(() => {
    if (resolvedName) {
      const splitName = resolvedName.split(" ");
      if (splitName.length > 1) {
        setAvatarLetters(splitName[0].charAt(0) + splitName[splitName.length - 1].charAt(0));
      } else {
        setAvatarLetters(splitName[0].charAt(0));
      }
    }
  }, [resolvedName]);

  return resolvedSquareImage ? (
    <div className={`relative inline-flex ${AvatarClassNamesMap[size]} ${className}`}>
      <img
        alt="avatar"
        src={resolvedSquareImage}
        className="w-full h-full object-cover rounded-lg bg-white shadow-md"
      />
    </div>
  ) : (
    <div
      className={clsx(
        "shadow-md flex items-center justify-center rounded-lg",
        AvatarClassNamesMap[size],
        className
      )}
    >
      <span className="text-black font-semibold">{avatarLetters}</span>
    </div>
  );
};
